import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Clock, ChevronLeft, Globe, Search, Monitor, Globe2 } from 'lucide-react';
import { useReportFilters } from './useReportFilters';
import { DateNavInput } from './DateNavInput';
import { todayInTimezone, formatUtcTime } from '@/utils/dateUtils';
import { toast } from 'sonner';

function formatSecs(s) {
  const sec = Math.max(0, Number(s || 0));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), ss = sec % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${ss}s`;
  return `${ss}s`;
}

const CAT_COLORS = {
  allowed: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  blocked: 'bg-red-100 text-red-700 border-red-200',
  unlisted: 'bg-gray-100 text-gray-600 border-gray-200',
};

// Deterministic color from string
function strColor(str) {
  const palette = ['#6366f1','#22c55e','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f43f5e','#6366f1','#3b82f6','#ec4899'];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xfffffff;
  return palette[h % palette.length];
}

const BROWSER_APPS = new Set([
  'chrome','google chrome','chromium','msedge','microsoft edge','edge',
  'firefox','mozilla firefox','safari','opera','brave','brave browser',
  'internet explorer','iexplore','arc','vivaldi','tor browser','yandex'
]);

function isBrowser(appName) {
  return BROWSER_APPS.has((appName || '').toLowerCase().trim());
}

export default function UsageTimelinePage() {
  const navigate = useNavigate();
  const { staffList, timezone, todayDate } = useReportFilters();

  const [searchQ, setSearchQ] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');

  // Set correct date once company timezone is known
  useEffect(() => {
    if (todayDate) setSelectedDate(todayDate);
  }, [todayDate]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const filteredStaff = staffList.filter(s =>
    !searchQ || s.name?.toLowerCase().includes(searchQ.toLowerCase())
  );

  useEffect(() => {
    if (staffList.length > 0 && !selectedStaff) setSelectedStaff(staffList[0]);
  }, [staffList]);

  useEffect(() => {
    if (selectedStaff) fetchTimeline();
  }, [selectedStaff, selectedDate]);

  const fetchTimeline = async () => {
    setLoading(true);
    setEvents([]);
    try {
      const res = await axios.get(`${API}/admin/activity-reports/timeline`, {
        params: { date: selectedDate, staff_id: selectedStaff?.staff_id },
        withCredentials: true
      });
      // Filter only app_usage type events from the flat timeline
      const all = (res.data?.timeline || []).filter(ev => ev.type === 'app_usage');
      // Sort by time desc
      all.sort((a, b) => new Date(b.start_time || b.time) - new Date(a.start_time || a.time));
      setEvents(all);
    } catch (e) {
      toast.error('Failed to load timeline');
    } finally { setLoading(false); }
  };

    const formatTime = (iso) => formatUtcTime(iso, '');


  return (
    <div style={{ height: 'calc(100vh - 80px)' }} className="flex flex-col" data-testid="usage-timeline-page">
      {/* Top bar */}
      <div className="flex items-center gap-3 mb-4 flex-shrink-0">
        <button onClick={() => navigate('/admin/reports')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Usage Timeline</h1>
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
            <Globe className="w-3.5 h-3.5" /><span>{timezone}</span>
          </div>
        </div>
        <div>
          <DateNavInput value={selectedDate} onChange={setSelectedDate} />
        </div>
      </div>

      <div className="flex gap-4 flex-1 min-h-0">
        {/* Left: Staff list */}
        <div className="w-56 flex-shrink-0 flex flex-col bg-white border rounded-xl overflow-hidden shadow-sm">
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search members" className="pl-8 h-8 text-sm" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredStaff.map(s => (
              <button
                key={s.staff_id}
                onClick={() => setSelectedStaff(s)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 ${selectedStaff?.staff_id === s.staff_id ? 'bg-blue-50 border-blue-100' : ''}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0`}
                  style={{ background: strColor(s.name || '') }}>
                  {s.name?.[0]?.toUpperCase() || '?'}
                </div>
                <span className={`text-sm truncate ${selectedStaff?.staff_id === s.staff_id ? 'font-semibold text-blue-700' : 'text-gray-700'}`}>{s.name}</span>
              </button>
            ))}
            {filteredStaff.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-4">No results found</p>
            )}
          </div>
        </div>

        {/* Right: Timeline */}
        <div className="flex-1 bg-white border rounded-xl shadow-sm overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b flex-shrink-0">
            <h2 className="font-semibold text-gray-800">
              {selectedStaff ? `${selectedStaff.name}'s app usage history` : 'Select a staff member'}
            </h2>
            {events.length > 0 && (
              <p className="text-xs text-gray-500 mt-0.5">{events.length} events on {selectedDate}</p>
            )}
          </div>

          {/* Timeline body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading && (
              <div className="text-center py-12 text-gray-500">Loading timeline...</div>
            )}

            {!loading && events.length === 0 && (
              <div className="text-center py-16">
                <Clock className="w-12 h-12 mx-auto text-gray-200 mb-3" />
                <p className="text-gray-500">No usage events found</p>
                <p className="text-xs text-gray-400 mt-1">Activity is recorded when the desktop agent is running</p>
              </div>
            )}

            {!loading && events.length > 0 && (
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 -translate-x-1/2" />

                <div className="space-y-0">
                  {events.map((ev, i) => {
                    const isLeft = i % 2 === 0;
                    const appName = ev.app_name || ev.domain || ev.url || 'Unknown';
                    const color = strColor(appName);
                    const cat = (ev.category || 'unlisted').toLowerCase();
                    const time = formatTime(ev.start_time || ev.time || ev.timestamp);
                    const duration = formatSecs(ev.duration_seconds || ev.usage_seconds || 0);

                    return (
                      <div key={i} className={`flex items-center mb-4 ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}>
                        {/* Card */}
                        <div className={`w-5/12 ${isLeft ? 'pr-6' : 'pl-6'}`}>
                          <div className={`bg-white border rounded-xl p-3 shadow-sm hover:shadow-md transition-shadow ${ev.domain ? 'border-l-4 border-l-blue-300' : ''}`}>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{ background: `${color}22` }}>
                                {isBrowser(ev.app_name) || ev.domain ? (
                                  <Globe2 className="w-4 h-4" style={{ color }} />
                                ) : (
                                  <Monitor className="w-4 h-4" style={{ color }} />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">{appName}</p>
                                <p className="text-xs text-gray-500">{duration}</p>
                              </div>
                            </div>
                            {/* URL / domain row */}
                            {ev.domain && (
                              <div className="flex items-center gap-1 mt-1.5 px-0.5">
                                <Globe className="w-3 h-3 text-blue-400 flex-shrink-0" />
                                <span className="text-xs text-blue-600 truncate font-mono">{ev.domain}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              <Badge className={`text-[10px] border ${CAT_COLORS[cat] || CAT_COLORS.unlisted}`}>{cat}</Badge>
                              {isBrowser(ev.app_name) && !ev.domain && (
                                <span className="text-[10px] text-gray-400 italic">no URL tracked</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Center dot + time */}
                        <div className="w-2/12 flex flex-col items-center gap-1 relative z-10">
                          <div className="w-8 h-8 rounded-full bg-white border-2 shadow-sm flex items-center justify-center"
                            style={{ borderColor: color }}>
                            <Clock className="w-3.5 h-3.5" style={{ color }} />
                          </div>
                          <span className="text-xs text-gray-500 font-mono whitespace-nowrap">{time}</span>
                        </div>

                        {/* Spacer */}
                        <div className="w-5/12" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
